import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { TECHCHANGE_DEFAULT_ASPECT_RATIO_BOOTSTRAP } from '../../../_shared';
import { messages } from '../../../_shared/i18n/messages';

import { ENUMS_VIDEO, MUX_PLAYER_STREAM_TYPE } from '../../constants';
import videoTypes from '../../types';
import LibraryPlayerAlert from '../LibraryPlayerAlert';
import MuxPlayer from '../MuxPlayer';

const propTypes = {

	/**
	 * The video for playback, if any.
	 */
	video: videoTypes.video,

	/**
	 * The input error, if any.
	 */
	error: PropTypes.string,

	/**
	 * True if the current user is an admin.
	 */
	isAdmin: PropTypes.bool.isRequired,

	/**
	 * The title of the video. Used for metrics in Mux Data.
	 */
	title: PropTypes.string,

	/**
	 * The content type containing the video (e.g. event, resource, discussion thread).
	 * Used for metrics in Mux Data.
	 */
	contentType: PropTypes.string,

	/**
	 * The domain of the organization for the video. Used for metrics in Mux Data.
	 * Mux Data metric is called "Sub Property ID"
	 */
	orgDomain: PropTypes.string,

	/**
	 * The user ID of the user currently viewing the video, if any.
	 * Used for metrics in Mux Data.
	 */
	viewerUserId: PropTypes.string,

	/**
	 * The aspect ratio for the video. Defaults to 16x9.
	 */
	aspectRatio: PropTypes.string,

	/**
	 * Flag to auto play the video. Defaults to false.
	 */
	autoplay: PropTypes.bool,

	/**
	 * Callback function to handle video watching.
	 */
	onVideoWatch: PropTypes.func,

};

const defaultProps = {
	aspectRatio: TECHCHANGE_DEFAULT_ASPECT_RATIO_BOOTSTRAP,
	autoplay: false,
};

/**
 * Component that renders a video from our library.
 * Fetches the video based on the provided ID and scope.
 * Shows errors if encountered during fetch or missing data
 * is not provided.
 *
 * Currently the only supported Library video provider is Mux.
 * Currently the only supported stream type is on demand for playing
 * static videos.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}      The LibraryVideo component.
 */
const LibraryVideo = (props) => {

	const {
		video,
		error,
		isAdmin,
		viewerUserId,
		title,
		contentType,
		orgDomain,
		aspectRatio,
		autoplay,
		onVideoWatch,
	} = props;

	// If an error is encountered, show generic error
	if (error) {
		return <LibraryPlayerAlert />;
	}

	if (!video) { return null; }

	// Handle Mux as the Library video provider
	if (video.provider === ENUMS_VIDEO.PROVIDER.MUX) {

		// If the user is an admin and the video is not yet ready,
		// show the admin the status
		if (isAdmin &&
			video.status !== ENUMS_VIDEO.STATUS.READY
		) {
			return (
				<LibraryPlayerAlert>
					<FormattedMessage
						{...messages.TC_COMPONENTS_VIDEO_LIBRARY_VIDEO_STATUS_NOT_READY_ADMIN}
						values={{ status: video.status }} />
				</LibraryPlayerAlert>
			);
		}

		// If video is in a not ready but not errored state,
		// show an alert informing the user the content is not yet ready
		if (
			(video.playback_id_signed && video.mux_tokens) &&
			(video.status === ENUMS_VIDEO.STATUS.PREPARING || video.status === ENUMS_VIDEO.STATUS.WAITING)
		) {
			return (
				<LibraryPlayerAlert>
					<FormattedMessage {...messages.TC_COMPONENTS_VIDEO_LIBRARY_VIDEO_STATUS_NOT_READY_USER} />
				</LibraryPlayerAlert>
			);
		}

		// Show an error if the required Mux attributes are not
		// present or the video is not ready to play
		if (
			!video.playback_id_signed ||
			!video.mux_tokens ||
			video.status !== ENUMS_VIDEO.STATUS.READY
		) {
			return (
				<LibraryPlayerAlert>
					<FormattedMessage {...messages.TC_COMPONENTS_VIDEO_LIBRARY_VIDEO_ERROR} />
				</LibraryPlayerAlert>
			);
		}

		// Define common properties
		const commonProps = {
			id: video.id,
			title,
			contentType,
			streamType: MUX_PLAYER_STREAM_TYPE.ON_DEMAND,
			playbackId: video.playback_id_signed,
			tokens: video.mux_tokens,
			orgDomain,
			viewerUserId,
			aspectRatio,
			onVideoWatch
		};

		// Only include autoplay for event content
		return (
			contentType === 'event'
				? <MuxPlayer
						{...commonProps}
						autoplay={autoplay} />
				: <MuxPlayer {...commonProps} />
		);
	}

	return null;

};

LibraryVideo.propTypes = propTypes;
LibraryVideo.defaultProps = defaultProps;

export default LibraryVideo;
